import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useI18n, ROUTE_MAPPINGS } from '../context/I18nContext';
import { useCookies } from '../context/CookieContext';
import LanguageSwitcher from './LanguageSwitcher';
import { Calculator, RotateCcw, CheckCircle, CreditCard, MapPin, HelpCircle, Menu, X } from 'lucide-react';
import { Button } from './ui/button';

const Header = () => {
  const { t, locale } = useI18n();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  // Build path based on current locale
  const buildLocalePath = (routeKey) => {
    const route = ROUTE_MAPPINGS[locale]?.[routeKey] || ROUTE_MAPPINGS.it[routeKey];
    if (locale === 'it') {
      return route ? `/${route}` : '/';
    }
    return route ? `/${locale}/${route}` : `/${locale}`;
  };

  const navItems = [
    { routeKey: 'home', label: t('nav.calculate'), icon: Calculator },
    { routeKey: 'reverse', label: t('nav.reverse'), icon: RotateCcw },
    { routeKey: 'verify', label: t('nav.verify'), icon: CheckCircle },
    { routeKey: 'healthCard', label: t('nav.healthCard'), icon: CreditCard },
    { routeKey: 'municipalities', label: t('nav.municipalities'), icon: MapPin },
    { routeKey: 'howItWorks', label: t('nav.howItWorks'), icon: HelpCircle },
  ];

  const isActive = (routeKey) => {
    const path = buildLocalePath(routeKey);
    return location.pathname === path || 
           (routeKey === 'home' && (location.pathname === '/' || location.pathname === `/${locale}`));
  };

  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to={locale === 'it' ? '/' : `/${locale}`} className="flex items-center gap-2 group" data-testid="logo">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-emerald-700 rounded-xl flex items-center justify-center shadow-lg group-hover:shadow-emerald-200 transition-shadow">
              <span className="text-white font-bold text-lg">🇮🇹</span>
            </div>
            <div className="hidden sm:block">
              <span className="font-bold text-gray-900 text-lg">CodiceFiscale</span>
              <span className="text-emerald-600 font-bold">.expert</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-1">
            {navItems.map(({ routeKey, label, icon: Icon }) => {
              const path = buildLocalePath(routeKey);
              return (
                <Link
                  key={routeKey}
                  to={path}
                  className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive(routeKey)
                      ? 'bg-emerald-50 text-emerald-700'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  }`}
                  data-testid={`nav-${routeKey}`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="hidden xl:inline">{label}</span>
                </Link>
              );
            })}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2">
            <LanguageSwitcher />
            
            {/* Mobile menu button */}
            <Button
              variant="ghost"
              size="sm"
              className="lg:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <nav className="lg:hidden py-4 border-t border-gray-100" data-testid="mobile-menu">
            <div className="space-y-1">
              {navItems.map(({ routeKey, label, icon: Icon }) => {
                const path = buildLocalePath(routeKey);
                return (
                  <Link
                    key={routeKey}
                    to={path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                      isActive(routeKey)
                        ? 'bg-emerald-50 text-emerald-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`}
                  >
                    <Icon className="h-5 w-5" />
                    {label}
                  </Link>
                );
              })}
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
